import React, { useEffect, useState } from 'react';
import { Button, notification, Row, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { generateExportInvoice } from '@app/api/export.api';

interface Invoice {
  id: number;
  invoice_number: string;
  total: string;
  status: string;
  created_at: string;
}

interface PaymentsInfoProps {
  id: string | null;
  pr_code: string | null;
  invoice_data: Invoice[];
  ImpStatus?: string | null;
  wfActions?: string[];
  onMount?: () => void;
}

const PaymentsInfo: React.FC<PaymentsInfoProps> = ({ id, pr_code, invoice_data, ImpStatus, wfActions, onMount }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>(invoice_data || []);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const moduleType = 'EXPORT';

  useEffect(() => {
    console.log('Received code:', pr_code);
    setInvoices(invoice_data || []);
  }, [invoice_data, pr_code]);

  useEffect(() => {
    onMount?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showInvoice = (invoiceId: number, status: string, invoice_number: string, moduleType: string) => {
    if (id !== null) {
      navigate(`/import-invoice`, {
        state: {
          id,
          pr_code,
          invoiceId,
          status,
          invoice_number,
          ImpStatus,
          wfActions,
          moduleType,
        },
      });
    } else {
      notification.error({
        message: 'Error',
        description: 'Invalid ID or Invoice',
      });
    }
  };

  const handleGenerateInvoice = async () => {
    if (!id) return;

    try {
      setIsGeneratingInvoice(true);

      const response = await generateExportInvoice(Number(id), 'EXPORT');

      // Update according to your API response structure
      setInvoices(response.invoice_data || response.data || []);

      notification.success({
        message: 'Success',
        description: 'Invoice generated successfully.',
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to generate invoice.',
      });
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const invoiceColumns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <span style={{ color: status === 'PAID' ? 'green' : 'red' }}>{status}</span>,
    },
    {
      title: 'Total (LKR)',
      dataIndex: 'total',
      key: 'total',
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: Invoice) => (
        <Row align="middle">
          <Button
            size="small"
            type="primary"
            onClick={() => showInvoice(record.id, record.status, record.invoice_number, moduleType)}
          >
            View
          </Button>
        </Row>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Invoices</h3>

        {ImpStatus === 'PENDING_PAYMENT' && (!invoices || invoices.length === 0) && (
          <Button type="primary" loading={isGeneratingInvoice} onClick={handleGenerateInvoice}>
            Generate Invoice
          </Button>
        )}
      </Row>

      <Table dataSource={invoices} columns={invoiceColumns} rowKey="id" />

      <h3>Receipts</h3>
      <p>No receipts available yet.</p>
    </>
  );
};

export default PaymentsInfo;
